<template>
  <div class="panel-content-wrapper">
    <p class="title">{{ $t('app.campaign.management.informations.title') }}</p>
    <div class="separator"></div>
    <div>
      <p class="sub-section">{{ $t('app.campaign.management.informations.name') }}</p>
      <ConfirmInput class="campaign-name-input" input-class="light outline-white sm" v-model="state.campaignName" :placeholder="$t('app.campaign.management.informations.name')" @update:model-value="editCampaignName" ></ConfirmInput>
    </div>
    <div v-if="props?.campaign?.documents?.report" class="separator"></div>
    <div v-if="props?.campaign?.documents?.report">
      <p class="sub-section">{{ $t('app.campaign.newPage.slidingPanel.options.report.label') }}</p>
      <div class="choice">
        <input name="talent-report" type="radio" :value="{ send_to_talent: true, format: 'simplified' }" v-model="props.campaign.documents.report">{{ $t('app.campaign.newPage.slidingPanel.options.report.simplified') }}</input>
        <input name="talent-report" type="radio" :value="{ send_to_talent: true, format: 'complete' }" v-model="props.campaign.documents.report">{{ $t('app.campaign.newPage.slidingPanel.options.report.complete') }}</input>
        <input name="talent-report" type="radio" :value="{ send_to_talent: false, format: 'simplified' }" v-model="props.campaign.documents.report">{{ $t('app.campaign.newPage.slidingPanel.options.report.none') }}</input>
      </div>
    </div>
    <div class="separator"></div>
    <div>
      <div class="invite-via-link">
        <p class="sub-section">{{ $t("app.campaign.single.inviteByLink") }}</p>
        <div class="toggle-container">
          <div class="toggle" @click="togglePublicInvitation">
            <input type="checkbox" :checked="!!publicInvitation?.id" />
            <label />
          </div>
        </div>
      </div>
      <transition name="fade">
        <div class="spacing" v-if="publicInvitation?.id">
          <InputCopy class="link-title sm xs-text" :label="$t('app.campaign.single.campaignLink')" :value="campaignShareLink" />
          <p class="subtitle">{{ $t('app.campaign.single.talentActivationOptions') }}</p>
          <div class="radio-group">
            <label :class="['radio-option', 'radio-option-left', !publicInvitation?.requires_approval ? 'radio-option-selected' : '']">
              <div class="radio-header">
                <input class="auto-radio-button-dot" type="radio" :checked="!publicInvitation?.requires_approval" @change="toggleAutoActivation(true)" />
                <div class="radio-option-title s-text bold">{{ $t("app.campaign.single.autoActivate") }}</div>
              </div>
              <div>
                <div class="radio-option-text xs-text">
                  {{ $t("app.campaign.single.autoActivateDesc") }}
                </div>
              </div>
            </label>
            <label :class="['radio-option', !!publicInvitation?.requires_approval ? 'radio-option-selected' : '']">
              <div class="radio-header">
                <input class="auto-radio-button-dot" type="radio" :checked="!!publicInvitation?.requires_approval" @change="toggleAutoActivation(false)" />
                <div class="radio-option-title s-text bold">{{ $t("app.campaign.single.manualActivate") }}</div>
              </div>
              <div>
                <div class="radio-option-text xs-text">
                  {{ $t("app.campaign.single.manualActivateDesc") }}
                </div>
              </div>
            </label>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCampaign, useEditCampaign } from '@/queries/campaigns.query';
import { useCampaignPublicInvitation, useDisablePublicInvitation, useEnablePublicInvitation } from '@/queries/invitation.query';
import { useToast } from 'vue-toastification';
import ConfirmInput from '@/components/input/ConfirmInput.vue';
import { Campaign } from '@/types/campaign';

const emits = defineEmits(['close']);

const appStore = useAppStore();
const toast = useToast();
const { t } = useI18n();

const props = defineProps<{
  campaign: Campaign,
}>();

const { mutateAsync: editCampaign } = useEditCampaign();
const { data: publicInvitation, isPending: isPublicInvitationPending } = useCampaignPublicInvitation(props?.campaign?.id);

const { mutate: disablePublicInvitation } = useDisablePublicInvitation();
const { mutate: enablePublicInvitation } = useEnablePublicInvitation();

const state = reactive({
  campaignName: "",
});

function setCampaignName() {
  state.campaignName = props?.campaign?.campaign?.internal_name;
}

const campaignShareLink = computed(() => {
  if (!props.campaign || !publicInvitation?.value?.id)
    return null;

  return `${window.location.host.replace("app", "game")}/join?invitation_id=${
    publicInvitation.value.id
  }`;
});

function togglePublicInvitation() {
  if (publicInvitation?.value?.id) {
    disablePublicInvitation(
      { invitationId: publicInvitation.value.id },
      {
        onSuccess: () => {
          toast.success(t('app.campaign.management.toasts.publicLinkDisabled'));
        },
        onError: () => {
          toast.error(t('other.error.ERROR_UNKNOWN'));
        }
      }
    );
  } else if (props?.campaign?.id) {
    enablePublicInvitation(
      { campaignId: props.campaign.id },
      {
        onSuccess: () => {
          toast.success(t('app.campaign.management.toasts.publicLinkEnabled'));
        },
        onError: () => {
          toast.error(t('other.error.ERROR_UNKNOWN'));
        }
      }
    );
  }
}

function toggleAutoActivation(auto_activate: boolean) {
  editCampaign({
    context: {
      campaignId: props?.campaign?.id,
      spaceId: props?.campaign?.campaign.parent_workspace_id,
      organisationId: appStore.currentOrganisationId,
    },
    campaign: props.campaign,
    auto_activate: auto_activate,
  });
}

async function editCampaignName() {
  if (state?.campaignName === props?.campaign?.campaign?.internal_name) return;

  await editCampaign({
    context: {
      campaignId: props.campaign.id,
      organisationId: appStore.currentOrganisationId,
      spaceId: props.campaign.campaign.parent_workspace_id,
    },
    campaign: {
      schema_version: props.campaign.schema_version,
      id: props.campaign.id,
      campaign_version: props.campaign.campaign_version,
      branding: props.campaign.branding,
      assessment: props.campaign.assessment,
      campaign: {
        internal_name: state.campaignName,
      },
    }
  });
}

watch(props.campaign, () => {
  setCampaignName();
})

onMounted(() => {
  setCampaignName();
});
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.separator {
  width: 100%;
  height: 1px;
  background-color: var(--light-grey-color);
}

.panel-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 600px;
  padding: 1.5rem;

  .title {
    font-size: 20px;
    font-weight: 700;
  }

  .sub-section {
    font-weight: 600;
    margin-bottom: 12px;
  }

  .campaign-name-input {
    width: 100%;
  }

  .edit-buttons {
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 10px;
  }

  .choice {
    display: flex;
    align-items: end;
    gap: 10px;
  }

  .invite-via-link {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 12px;

    .sub-section {
      margin-bottom: 0px !important;
    }
  }

  .spacing {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .subtitle {
      font-size: 12px;
      color: var(--label-color);
    }
  }

  .radio-group {
      display: flex;
      align-items: stretch;
      gap: 8px;

      .radio-header {
        display: flex;
        gap: 8px;
        align-items: center;

        input {
          margin-top: 0;
        }
      }

      .radio-option {
        display: flex;
        flex-direction: column;
        gap: 8px;

        flex: 1;
        padding: 8px;
        border-radius: 4px;
        border: 1px solid var(--light-grey-color);
      }
    }
}
</style>